// src/domain/mappers.js
// Mappers del dominio: UI labels/inputs <-> valores DB (type/channel/stage/status/etc).
// Objetivo: centralizar conversiones y normalizaciones para evitar inconsistencias.
//
// Nota importante: NO asumo cambios en tu esquema. Esto es una capa "defensiva":
// - Si UI manda "Instagram" -> "instagram"
// - Si legacy manda "IG" -> "instagram"
// - Si DB trae null/"" -> defaults razonables
//
// Cuando conectemos con schema real de Supabase, afinamos y añadimos tests.

import {
  STAGES,
  STAGE_ORDER,
  EVENT_TYPES,
  CHANNELS,
  REGIONS,
  STATUSES,
  CONTACT_TYPES,
  CONTACT_ROLES,
  SUPPLIER_TYPES,
  LOGISTICS_CATEGORIES,
} from "./constants.js";

export { LOGISTICS_CATEGORIES };

// -------------------------
// Helpers de normalización
// -------------------------
export function normStr(v) {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

export function normKey(v) {
  // Normaliza para comparar claves/labels: lower, sin espacios extra
  return normStr(v).toLowerCase();
}

export function pickFirstDefined(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return undefined;
}

// -------------------------
// Generic mapping utilities
// -------------------------
/**
 * Crea un mapper por diccionario con normalización.
 * dict: { "instagram": "instagram", "ig": "instagram", "insta": "instagram", "Instagram": "instagram" }
 */
export function createDictMapper(dict, { defaultValue = null } = {}) {
  const normalized = {};
  for (const [k, v] of Object.entries(dict || {})) {
    normalized[normKey(k)] = v;
  }

  return function map(value) {
    const key = normKey(value);
    if (!key) return defaultValue;
    return normalized[key] ?? defaultValue;
  };
}

/**
 * Mapea a uno de los valores de un enum (Object.values(enum)).
 * - Si ya es un valor válido, lo devuelve.
 * - Si no, intenta resolver por alias.
 */
export function mapToEnum(value, enumObj, aliasDict = {}, { defaultValue = null } = {}) {
  const allowed = new Set(Object.values(enumObj));
  const v = normKey(value);

  if (!v) return defaultValue;

  // si el value original coincide con un valor del enum
  if (allowed.has(v)) return v;

  // si coincide case-sensitive con alguno (por si ya viene exacto)
  if (allowed.has(normStr(value))) return normStr(value);

  // alias mapping
  const aliasMapper = createDictMapper(aliasDict, { defaultValue });
  const mapped = aliasMapper(value);

  if (mapped && allowed.has(mapped)) return mapped;

  return defaultValue;
}

// -------------------------
// Stage / Status mapping
// -------------------------
// Alias del nuevo pipeline comercial (incluye compatibilidad legacy)
const STAGE_ALIASES = {
  // --- Lead ---
  lead: STAGES.LEAD,
  "new": STAGES.LEAD,
  nuevo: STAGES.LEAD,
  solicitud: STAGES.LEAD,

  // --- Info needed (absorbe contacted + meeting legacy) ---
  info_needed: STAGES.INFO_NEEDED,
  contacted: STAGES.INFO_NEEDED,      // legacy → info_needed
  contactado: STAGES.INFO_NEEDED,     // legacy → info_needed
  meeting: STAGES.INFO_NEEDED,        // legacy → info_needed
  reunion: STAGES.INFO_NEEDED,        // legacy → info_needed
  llamada: STAGES.INFO_NEEDED,        // legacy → info_needed
  "info pendiente": STAGES.INFO_NEEDED,

  // --- Proposal sent ---
  proposal: STAGES.PROPOSAL,
  proposal_sent: STAGES.PROPOSAL,
  presupuesto: STAGES.PROPOSAL,
  "presupuesto enviado": STAGES.PROPOSAL,

  // --- Negotiation ---
  negotiation: STAGES.NEGOTIATION,
  negociacion: STAGES.NEGOTIATION,
  negociación: STAGES.NEGOTIATION,
  dudas: STAGES.NEGOTIATION,

  // --- Accepted pending deposit (cliente aceptó, falta señal) ---
  accepted_pending_deposit: STAGES.ACCEPTED_PENDING_DEPOSIT,
  "aceptado señal pendiente": STAGES.ACCEPTED_PENDING_DEPOSIT,
  "aceptado pendiente": STAGES.ACCEPTED_PENDING_DEPOSIT,
  // Legacy: "pendiente_pago" tenía sentido similar
  pendiente_pago: STAGES.ACCEPTED_PENDING_DEPOSIT,

  // --- Confirmed (reservado CON señal) ---
  confirmed: STAGES.CONFIRMED,
  confirmado: STAGES.CONFIRMED,
  booked: STAGES.CONFIRMED,
  reservado: STAGES.CONFIRMED,
  // Legacy "cerrado" = confirmado con señal
  cerrado: STAGES.CONFIRMED,
  closed: STAGES.CONFIRMED,

  // --- Done ---
  done: STAGES.DONE,
  realizado: STAGES.DONE,

  // --- Lost / Rechazado ---
  lost: STAGES.LOST,
  perdido: STAGES.LOST,
  descartado: STAGES.LOST,
  rechazado: STAGES.LOST,
  rejected: STAGES.LOST,
  "no disponible": STAGES.LOST,
  fecha_ocupada: STAGES.LOST,
};

const STATUS_ALIASES = {
  open: STATUSES.OPEN,
  abierto: STATUSES.OPEN,

  won: STATUSES.CLOSED_WON,
  ganado: STATUSES.CLOSED_WON,
  "closed won": STATUSES.CLOSED_WON,
  confirmed: STATUSES.CLOSED_WON,

  lost: STATUSES.CLOSED_LOST,
  perdido: STATUSES.CLOSED_LOST,
  "closed lost": STATUSES.CLOSED_LOST,
};

// Si tu legacy tiene status antiguo y quieres derivar stage:
export function stageFromStatus(status) {
  const s = mapToEnum(status, STATUSES, STATUS_ALIASES, { defaultValue: null });
  if (!s) return null;

  if (s === STATUSES.CLOSED_WON) return STAGES.DONE;
  if (s === STATUSES.CLOSED_LOST) return STAGES.LOST;
  return STAGES.LEAD;
}

/**
 * Devuelve stage "seguro":
 * - usa stage si existe
 * - si no, deriva de status
 * - si no, default lead
 */
export function normalizeStage({ stage, status } = {}) {
  const st = mapToEnum(stage, STAGES, STAGE_ALIASES, { defaultValue: null });
  if (st) return st;

  const derived = stageFromStatus(status);
  return derived || STAGES.LEAD;
}

/**
 * Ordena stages según STAGE_ORDER
 */
export function compareStage(a, b) {
  const ia = STAGE_ORDER.indexOf(a);
  const ib = STAGE_ORDER.indexOf(b);
  return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
}

// -------------------------
// Type mapping
// -------------------------
const TYPE_ALIASES = {
  concierto: EVENT_TYPES.CONCERT,
  concert: EVENT_TYPES.CONCERT,

  festival: EVENT_TYPES.FESTIVAL,

  privado: EVENT_TYPES.PRIVATE,
  private: EVENT_TYPES.PRIVATE,

  empresa: EVENT_TYPES.CORPORATE,
  corporate: EVENT_TYPES.CORPORATE,

  boda: EVENT_TYPES.WEDDING,
  wedding: EVENT_TYPES.WEDDING,

  otro: EVENT_TYPES.OTHER,
  other: EVENT_TYPES.OTHER,
};

export function normalizeEventType(type) {
  return (
    mapToEnum(type, EVENT_TYPES, TYPE_ALIASES, { defaultValue: EVENT_TYPES.OTHER }) ||
    EVENT_TYPES.OTHER
  );
}

// -------------------------
// Channel mapping
// -------------------------
const CHANNEL_ALIASES = {
  ig: CHANNELS.INSTAGRAM,
  insta: CHANNELS.INSTAGRAM,
  instagram: CHANNELS.INSTAGRAM,

  whatsapp: CHANNELS.WHATSAPP,
  wa: CHANNELS.WHATSAPP,
  wpp: CHANNELS.WHATSAPP,

  email: CHANNELS.EMAIL,
  mail: CHANNELS.EMAIL,

  telefono: CHANNELS.PHONE,
  teléfono: CHANNELS.PHONE,
  phone: CHANNELS.PHONE,
  llamada: CHANNELS.PHONE,

  referral: CHANNELS.REFERRAL,
  referido: CHANNELS.REFERRAL,
  recomendacion: CHANNELS.REFERRAL,
  recomendación: CHANNELS.REFERRAL,

  web: CHANNELS.WEB,
  formulario: CHANNELS.WEB,

  agencia: CHANNELS.AGENCY,
  agency: CHANNELS.AGENCY,

  otro: CHANNELS.OTHER,
  other: CHANNELS.OTHER,
};

export function normalizeChannel(channel) {
  return (
    mapToEnum(channel, CHANNELS, CHANNEL_ALIASES, { defaultValue: CHANNELS.OTHER }) ||
    CHANNELS.OTHER
  );
}

// -------------------------
// Region mapping
// -------------------------
const REGION_ALIASES = {
  vizcaya: REGIONS.BIZKAIA,
  bizkaia: REGIONS.BIZKAIA,
  bilbao: REGIONS.BIZKAIA,
  getxo: REGIONS.BIZKAIA,

  gipuzkoa: REGIONS.GIPUZKOA,
  guipuzcoa: REGIONS.GIPUZKOA,
  donostia: REGIONS.GIPUZKOA,
  sansebastian: REGIONS.GIPUZKOA,
  "san sebastian": REGIONS.GIPUZKOA,

  alava: REGIONS.ARABA,
  áraba: REGIONS.ARABA,
  araba: REGIONS.ARABA,
  vitoria: REGIONS.ARABA,

  navarra: REGIONS.NAVARRA,
  pamplona: REGIONS.NAVARRA,

  cantabria: REGIONS.CANTABRIA,
  santander: REGIONS.CANTABRIA,

  burgos: REGIONS.BURGOS,

  "la rioja": REGIONS.LA_RIOJA,
  logroño: REGIONS.LA_RIOJA,
  logrono: REGIONS.LA_RIOJA,

  other: REGIONS.OTHER,
  otro: REGIONS.OTHER,
};

export function normalizeRegion(region) {
  return (
    mapToEnum(region, REGIONS, REGION_ALIASES, { defaultValue: REGIONS.OTHER }) ||
    REGIONS.OTHER
  );
}

// -------------------------
// Contact type mapping
// -------------------------
const CONTACT_TYPE_ALIASES = {
  pareja: CONTACT_TYPES.COUPLE,
  couple: CONTACT_TYPES.COUPLE,

  wedding: CONTACT_TYPES.WEDDING_PLANNER,
  wedding_planner: CONTACT_TYPES.WEDDING_PLANNER,
  planner: CONTACT_TYPES.WEDDING_PLANNER,

  venue: CONTACT_TYPES.VENUE_MANAGER,
  venue_manager: CONTACT_TYPES.VENUE_MANAGER,

  company: CONTACT_TYPES.COMPANY,
  empresa: CONTACT_TYPES.COMPANY,

  other: CONTACT_TYPES.OTHER,
  otro: CONTACT_TYPES.OTHER,
};

export function normalizeContactType(type) {
  return (
    mapToEnum(type, CONTACT_TYPES, CONTACT_TYPE_ALIASES, { defaultValue: CONTACT_TYPES.OTHER }) ||
    CONTACT_TYPES.OTHER
  );
}

// -------------------------
// Contact role mapping
// -------------------------
const CONTACT_ROLE_ALIASES = {
  principal: CONTACT_ROLES.MAIN,
  main: CONTACT_ROLES.MAIN,

  secondary: CONTACT_ROLES.SECONDARY,
  secundario: CONTACT_ROLES.SECONDARY,

  billing: CONTACT_ROLES.BILLING,
  facturacion: CONTACT_ROLES.BILLING,
  facturación: CONTACT_ROLES.BILLING,

  tech: CONTACT_ROLES.TECH,
  tecnico: CONTACT_ROLES.TECH,
  técnico: CONTACT_ROLES.TECH,

  other: CONTACT_ROLES.OTHER,
  otro: CONTACT_ROLES.OTHER,
};

export function normalizeContactRole(role) {
  return (
    mapToEnum(role, CONTACT_ROLES, CONTACT_ROLE_ALIASES, { defaultValue: CONTACT_ROLES.OTHER }) ||
    CONTACT_ROLES.OTHER
  );
}

// -------------------------
// Supplier type mapping
// -------------------------
const SUPPLIER_TYPE_ALIASES = {
  furgoneta: SUPPLIER_TYPES.TRANSPORT,
  furgo: SUPPLIER_TYPES.TRANSPORT,
  van: SUPPLIER_TYPES.TRANSPORT,

  sonido: SUPPLIER_TYPES.SOUND,
  sound: SUPPLIER_TYPES.SOUND,

  tarima: SUPPLIER_TYPES.STAGE,
  stage: SUPPLIER_TYPES.STAGE,

  luces: SUPPLIER_TYPES.LIGHTING,
  iluminacion: SUPPLIER_TYPES.LIGHTING,
  lighting: SUPPLIER_TYPES.LIGHTING,

  catering: SUPPLIER_TYPES.CATERING,
  hotel: SUPPLIER_TYPES.HOTEL,

  transporte: SUPPLIER_TYPES.TRANSPORT,
  transport: SUPPLIER_TYPES.TRANSPORT,

  other: SUPPLIER_TYPES.OTHER,
  otro: SUPPLIER_TYPES.OTHER,
};

export function normalizeSupplierType(type) {
  const value = mapToEnum(type, SUPPLIER_TYPES, SUPPLIER_TYPE_ALIASES, { defaultValue: null });
  return value || null;
}

export function normalizeSupplierTypes(types) {
  const rawValues = Array.isArray(types)
    ? types
    : normStr(types).split(",").map(item => item.trim());
  const seen = new Set();
  const normalized = [];

  rawValues.forEach((type) => {
    const value = normalizeSupplierType(type);
    if (!value || seen.has(value)) return;
    seen.add(value);
    normalized.push(value);
  });

  return normalized;
}

// -------------------------
// UI <-> DB field mapping (helpers)
// -------------------------

/**
 * Limpia inputs típicos antes de enviar a DB:
 * - trim strings
 * - "" -> null si allowEmpty=false
 */
export function cleanText(value, { allowEmpty = false } = {}) {
  const s = normStr(value);
  if (!allowEmpty && s === "") return null;
  return s;
}

/**
 * Parsea números seguros desde UI (inputs)
 */
export function toNumber(value, { defaultValue = null } = {}) {
  if (value === null || value === undefined || value === "") return defaultValue;
  const n = Number(value);
  return Number.isFinite(n) ? n : defaultValue;
}

/**
 * Parsea booleanos típicos (checkbox, "true"/"false")
 */
export function toBool(value, { defaultValue = false } = {}) {
  if (value === true || value === false) return value;
  const v = normKey(value);
  if (!v) return defaultValue;
  if (["1", "true", "yes", "si", "sí", "on"].includes(v)) return true;
  if (["0", "false", "no", "off"].includes(v)) return false;
  return defaultValue;
}

/**
 * Fecha UI -> ISO (yyyy-mm-dd) si viene como Date o string
 * Nota: NO añade zona horaria; mantiene formato simple si ya lo tienes así.
 */
export function toDateISO(value, { defaultValue = null } = {}) {
  if (!value) return defaultValue;

  if (value instanceof Date && !isNaN(value.getTime())) {
    // yyyy-mm-dd
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const s = normStr(value);
  // Si ya viene como yyyy-mm-dd, lo devolvemos
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // Intento de parseo
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  return defaultValue;
}

// -------------------------
// Normalizadores de entidades (defensivos)
// -------------------------

/**
 * Normaliza un evento "crudo" a un shape seguro para UI:
 * - stage normalizado
 * - type/channel/region normalizados si existen
 * - strings trimmed
 */
export function normalizeEvent(ev = {}) {
  const out = { ...ev };

  // stage/status
  out.stage = normalizeStage({ stage: ev.stage, status: ev.status });

  // opcionales
  if ("type" in ev) out.type = normalizeEventType(ev.type);
  if ("channel" in ev) out.channel = normalizeChannel(ev.channel);
  if ("region" in ev) out.region = normalizeRegion(ev.region);

  // textos comunes
  if ("name" in ev) out.name = cleanText(ev.name, { allowEmpty: false });
  if ("notes" in ev) out.notes = cleanText(ev.notes, { allowEmpty: true });

  // fecha si hay
  if ("date" in ev) out.date = toDateISO(ev.date, { defaultValue: ev.date ?? null });

  return out;
}

/**
 * Normaliza un contacto
 */
export function normalizeContact(c = {}) {
  const out = { ...c };
  if ("name" in c) out.name = cleanText(c.name, { allowEmpty: false });
  if ("email" in c) out.email = cleanText(c.email, { allowEmpty: false });
  if ("phone" in c) out.phone = cleanText(c.phone, { allowEmpty: false });
  if ("role" in c) out.role = normalizeContactRole(c.role);
  return out;
}

/**
 * Normaliza un venue
 */
export function normalizeVenue(v = {}) {
  const out = { ...v };
  if ("name" in v) out.name = cleanText(v.name, { allowEmpty: false });
  if ("address" in v) out.address = cleanText(v.address, { allowEmpty: true });
  if ("region" in v) out.region = normalizeRegion(v.region);
  return out;
}
